import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import MobileMenu from '../../components/feature/MobileMenu';

export default function HomePage() {
  useSEO({
    title: 'Mr. EcoClean - Professional Solar Panel Cleaning, Window Washing & Bird Proofing Sacramento CA',
    description: 'Professional solar panel cleaning, window washing, and bird proofing services in Sacramento CA. Maximize solar efficiency, crystal clear windows, and protect your property. Eco-friendly, licensed & insured. Free quotes available.',
    keywords: 'solar panel cleaning Sacramento, window washing Sacramento, bird proofing Sacramento, solar panel maintenance, residential window cleaning, commercial window washing, pigeon control',
    canonical: '/',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "name": "Mr. EcoClean",
          "description": "Professional solar panel cleaning, window washing, and bird proofing services for residential and commercial properties in Sacramento CA",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://mrecoclean.com'}`,
          "telephone": "+19168366145",
          "email": "Jason@mrecoclean.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Sacramento",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 38.5816,
            "longitude": -121.4944
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "07:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "08:00",
              "closes": "16:00"
            }
          ],
          "priceRange": "$$",
          "image": "https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/1fe53895dbc344f81e4ae10fc5461665.jpeg",
          "serviceArea": {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": 38.5816,
              "longitude": -121.4944
            },
            "geoRadius": "50000"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Cleaning Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Solar Panel Cleaning",
                  "description": "Professional solar panel cleaning to maximize energy efficiency"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Window Washing",
                  "description": "Professional residential and commercial window cleaning services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Bird Proofing",
                  "description": "Professional bird control and proofing solutions for solar panels and buildings"
                }
              }
            ]
          }
        },
        {
          "@type": "WebSite",
          "name": "Mr. EcoClean",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://mrecoclean.com'}`,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${import.meta.env.VITE_SITE_URL || 'https://mrecoclean.com'}/services?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-red-600" style={{ fontFamily: '"Pacifico", serif' }}>
                Mr. EcoClean
              </h1>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-red-600 font-medium whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/services" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Services</Link>
              <Link to="/areas-covered" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Areas Covered</Link>
              <Link to="/about" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">About</Link>
              <Link to="/contact" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Contact</Link>
            </div>
            <MobileMenu />
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/a8e638ebf90e588fbd63e7493e0d1a37.png)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Professional Solar Panel Cleaning &amp; Bird Proofing
          </h2>
          <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
            Maximize your solar efficiency with eco-friendly cleaning, crystal-clear window washing, and bird proofing throughout Sacramento and surrounding areas. Licensed, insured, and satisfaction guaranteed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a 
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Professional Services</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive solar panel cleaning, window washing, and bird proofing solutions for residential and commercial properties throughout Sacramento and surrounding areas.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-sun-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Solar Panel Cleaning</h4>
              <p className="text-gray-600 mb-6">
                Professional solar panel cleaning to maximize energy efficiency and extend panel lifespan. Remove dirt, dust, and debris for optimal performance.
              </p>
              <Link to="/services" className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer">
                Learn More →
              </Link>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-window-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Window Washing</h4>
              <p className="text-gray-600 mb-6">
                Crystal clear window cleaning for homes and businesses. Professional streak-free results for all window types and sizes.
              </p>
              <Link to="/services" className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer">
                Learn More →
              </Link>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Bird Proofing</h4>
              <p className="text-gray-600 mb-6">
                Protect your solar panels and property from bird damage. Professional bird control and proofing solutions that last.
              </p>
              <Link to="/services" className="text-sky-600 font-semibold hover:text-sky-700 cursor-pointer">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Mr. EcoClean?</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">Licensed & Insured</h5>
                    <p className="text-gray-600">Fully licensed and insured for your peace of mind and protection.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">Professional Equipment</h5>
                    <p className="text-gray-600">State-of-the-art cleaning equipment and eco-friendly solutions for superior results.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">Satisfaction Guaranteed</h5>
                    <p className="text-gray-600">100% satisfaction guarantee on all our cleaning services.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sky-600"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">Local Sacramento Business</h5>
                    <p className="text-gray-600">Proudly serving Sacramento and surrounding communities with reliable service.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/1fe53895dbc344f81e4ae10fc5461665.jpeg"
                alt="Professional solar panel cleaning service in Sacramento"
                className="rounded-xl shadow-lg w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Service Areas</h3>
            <p className="text-xl text-gray-600">
              We proudly serve Sacramento and surrounding communities
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <i className="ri-map-pin-line text-3xl text-sky-600 mb-4"></i>
              <h5 className="font-semibold text-gray-900">Sacramento</h5>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <i className="ri-map-pin-line text-3xl text-sky-600 mb-4"></i>
              <h5 className="font-semibold text-gray-900">Elk Grove</h5>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <i className="ri-map-pin-line text-3xl text-sky-600 mb-4"></i>
              <h5 className="font-semibold text-gray-900">Roseville</h5>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <i className="ri-map-pin-line text-3xl text-sky-600 mb-4"></i>
              <h5 className="font-semibold text-gray-900">Folsom</h5>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link 
              to="/areas-covered" 
              className="inline-block bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Service Areas
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Property?</h3>
          <p className="text-xl text-red-100 mb-8">
            Get a free quote today for solar panel cleaning, window washing, or bird proofing services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a 
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now: (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-sky-400 mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                Mr. EcoClean
              </h4>
              <p className="text-gray-300 mb-4">
                Professional solar panel cleaning, window washing, and bird proofing services for Sacramento and surrounding areas.
              </p>
              <div className="flex space-x-4">
                <i className="ri-facebook-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-instagram-line text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-google-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Solar Panel Cleaning</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Window Washing</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Bird Proofing</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Contact Info</h5>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  <a href="tel:916-836-6145" className="hover:text-sky-400 cursor-pointer">(916) 836-6145</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:Jason@mrecoclean.com" className="hover:text-sky-400 cursor-pointer">Jason@mrecoclean.com</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Sacramento, CA
                </p>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Business Hours</h5>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                <p>Saturday: 8:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Mr. EcoClean. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-sky-400 cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
